import { useEffect, useRef, useState, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider, useLang } from "@/context/LanguageContext";
import api from "@/lib/api";
import { roleHome } from "@/lib/roles";

// Route-basiertes Code-Splitting: nur der aktuell benötigte Bereich wird geladen.
// Reduziert den Erst-Download deutlich (v. a. auf Mobilgeräten).
const AppShell = lazy(() => import("@/components/AppShell"));
const Login = lazy(() => import("@/pages/Login"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const KalenderPro = lazy(() => import("@/pages/KalenderPro"));
const KalenderCockpit = lazy(() => import("@/pages/KalenderCockpit"));
const Freigaben = lazy(() => import("@/pages/Freigaben"));
const FreigabenAmpel = lazy(() => import("@/pages/FreigabenAmpel"));
const AuditLog = lazy(() => import("@/pages/AuditLog"));
const Kunden = lazy(() => import("@/pages/Kunden"));
const KundeAnlegen = lazy(() => import("@/pages/KundeAnlegen"));
const KundenDetail = lazy(() => import("@/pages/KundenDetail"));
const Leads = lazy(() => import("@/pages/Leads"));
const WordPress = lazy(() => import("@/pages/WordPress"));
const Integrationen = lazy(() => import("@/pages/Integrationen"));
const Abos = lazy(() => import("@/pages/Abos"));
const Report = lazy(() => import("@/pages/Report"));
const Support = lazy(() => import("@/pages/Support"));
const SupportCockpit = lazy(() => import("@/pages/SupportCockpit"));
const Pocket = lazy(() => import("@/pages/Pocket"));
const FotoAuftraege = lazy(() => import("@/pages/FotoAuftraege"));
const PublicUpload = lazy(() => import("@/pages/PublicUpload"));
const MarkenProfil = lazy(() => import("@/pages/MarkenProfil"));
const Signup = lazy(() => import("@/pages/Signup"));
const RollenRechte = lazy(() => import("@/pages/RollenRechte"));
const Einstellungen = lazy(() => import("@/pages/Einstellungen"));
const Inhalte = lazy(() => import("@/pages/Inhalte"));
const CockpitShell = lazy(() => import("@/components/CockpitShell"));
const ClientCockpit = lazy(() => import("@/pages/ClientCockpit"));
const SystemHealth = lazy(() => import("@/pages/SystemHealth"));
const Aufgaben = lazy(() => import("@/pages/Aufgaben"));
const Nachrichten = lazy(() => import("@/pages/Nachrichten"));
const Rechnungen = lazy(() => import("@/pages/Rechnungen"));
const FinanzDashboard = lazy(() => import("@/pages/FinanzDashboard"));
const Abrechnungseinstellungen = lazy(() => import("@/pages/Abrechnungseinstellungen"));
const Preise = lazy(() => import("@/pages/Preise"));
const AboErlebnis = lazy(() => import("@/pages/AboErlebnis"));
const SpeicherMedien = lazy(() => import("@/pages/SpeicherMedien"));
const AboSpeicher = lazy(() => import("@/pages/AboSpeicher"));
const ClientBeitraege = lazy(() => import("@/pages/ClientBeitraege"));
const ChannelAddons = lazy(() => import("@/pages/ChannelAddons"));
const ClientRechnungen = lazy(() => import("@/pages/ClientRechnungen"));
const Force2FASetup = lazy(() => import("@/pages/Force2FASetup"));
const Kundenfeedback = lazy(() => import("@/pages/Kundenfeedback"));
const Zahlungen = lazy(() => import("@/pages/Zahlungen"));
const Zusatzleistungen = lazy(() => import("@/pages/Zusatzleistungen"));
const Backups = lazy(() => import("@/pages/Backups"));
const DemoAnfragen = lazy(() => import("@/pages/DemoAnfragen"));
const DemoZugaenge = lazy(() => import("@/pages/DemoZugaenge"));
const MetaConnectPublic = lazy(() => import("@/pages/MetaConnectPublic"));
const MetaConnectDone = lazy(() => import("@/pages/MetaConnectPublic").then((m) => ({ default: m.MetaConnectDone })));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const BreakGlass = lazy(() => import("@/pages/BreakGlass"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const PasswortZuruecksetzen = lazy(() => import("@/pages/PasswortZuruecksetzen"));
const RecoveryGuardian = lazy(() => import("@/pages/RecoveryGuardian"));
const RecoveryReset = lazy(() => import("@/pages/RecoveryReset"));
const RecoverySetup = lazy(() => import("@/pages/RecoverySetup"));
const JuryDemo = lazy(() => import("@/pages/JuryDemo"));
const Kennenlernen = lazy(() => import("@/pages/Kennenlernen"));
const JuryIntegrationen = lazy(() => import("@/pages/JuryIntegrationen"));
const JuryControlOS = lazy(() => import("@/pages/JuryControlOS"));
const JuryMedien = lazy(() => import("@/pages/JuryMedien"));
const JuryPocket = lazy(() => import("@/pages/JuryPocket"));

function FullLoader() {
  const { lang } = useLang();
  const L = { de: "Lädt…", fr: "Chargement…", it: "Caricamento…", en: "Loading…" };
  return (
    <div className="flex h-screen items-center justify-center bg-background">
      <div className="flex items-center gap-3 text-muted-foreground">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-border border-t-primary" />
        <span className="text-sm">{L[lang] || L.de}</span>
      </div>
    </div>
  );
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AuthCallback() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const done = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = window.location.hash;
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        await api.post("/auth/session", { session_id: sid });
        const data = await checkAuth();
        window.history.replaceState(null, "", window.location.pathname);
        navigate(roleHome(data?.user?.global_role), { replace: true });
      } catch {
        setError("Google-Anmeldung fehlgeschlagen.");
      }
    })();
  }, [checkAuth, navigate]);

  if (error)
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
        {error}
        <a href="/login" className="underline">Zurück zum Login</a>
      </div>
    );
  return <FullLoader />;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading || user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  // Recovery-Guardian: strikt isoliertes Notfall-Konto — nur die Wiederherstellungsseite.
  if (user.global_role === "recovery_guardian" && !location.pathname.startsWith("/kontowiederherstellung"))
    return <Navigate to="/kontowiederherstellung" replace />;
  // Pflicht-2FA: Benutzer mit erzwungener, aber nicht eingerichteter 2FA werden auf die
  // Einrichtungsseite umgeleitet, bevor sie die App nutzen können.
  if (user.mfa_enrollment_required && location.pathname !== "/2fa-pflicht")
    return <Navigate to="/2fa-pflicht" replace />;
  return children;
}

// Recovery-Only: ausschliesslich das Notfall-Konto (recovery_guardian).
function RecoveryOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.global_role !== "recovery_guardian") return <Navigate to="/dashboard" replace />;
  return children;
}

// Super-Admin-only (Systeminhaber): z. B. Notfall-Wiederherstellung einrichten.
function SuperAdminOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.global_role !== "super_admin") return <Navigate to="/dashboard" replace />;
  return children;
}

const STAFF_ROLES = ["super_admin", "kewag_admin", "editor", "support", "kewag_finanzen"];
const ADMIN_ROLES = ["super_admin", "kewag_admin"];
const FINANCE_ROLES = ["super_admin", "kewag_admin", "kewag_finanzen"];
const isStaffRole = (r) => STAFF_ROLES.includes(r);

// Staff-only routes: clients (reviewer/viewer) are redirected to their cockpit.
function StaffOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!STAFF_ROLES.includes(user.global_role)) return <Navigate to="/dashboard" replace />;
  return children;
}

// Admin-only routes (KEWAG Admin / Super Admin): editor/support and clients are redirected.
function AdminOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!ADMIN_ROLES.includes(user.global_role)) return <Navigate to="/dashboard" replace />;
  return children;
}

// Finanz-Routen (Rechnungen/Abos/Abrechnung/Zahlungen + Kunden-Stammdaten lesen):
// Super Admin, KEWAG Admin und KEWAG Finanzen. editor/support/clients werden umgeleitet.
function FinanceOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!FINANCE_ROLES.includes(user.global_role)) return <Navigate to="/dashboard" replace />;
  return children;
}

// Kunden-Cockpit shell: echte Kunden ODER Staff in sicherer Kundensicht.
function CockpitGuard({ children }) {
  const { user, loading, kundensicht } = useAuth();
  if (loading || user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isStaffRole(user.global_role) || kundensicht?.active) return children;
  return <Navigate to={roleHome(user.global_role)} replace />;   // Staff ohne aktive Kundensicht → Control OS
}

// Staff Control-OS shell: echte Kunden und Staff-in-Kundensicht werden ins Cockpit geleitet.
function StaffShellGuard({ children }) {
  const { user, loading, kundensicht } = useAuth();
  if (loading || user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isStaffRole(user.global_role)) return <Navigate to="/cockpit/dashboard" replace />;
  if (kundensicht?.active) return <Navigate to="/cockpit/dashboard" replace />;
  return children;
}

// Rollen-/Kundensicht-bewusster Fallback für unbekannte Pfade.
function RoleFallback() {
  const { user, loading, kundensicht } = useAuth();
  if (loading || user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.global_role === "recovery_guardian") return <Navigate to="/kontowiederherstellung" replace />;
  if (!isStaffRole(user.global_role) || kundensicht?.active) return <Navigate to="/cockpit/dashboard" replace />;
  return <Navigate to={roleHome(user.global_role)} replace />;
}

// The public introduction never starts a jury session. Existing invitation,
// session-end and conclusion URLs retain the original isolated jury flow.
function PublicWalkthrough() {
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  return ["invite", "done", "ended", "legacy"].some(key => query.has(key))
    ? <JuryDemo /> : <Kennenlernen />;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Suspense fallback={<FullLoader />}>
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/jury-demo" element={<PublicWalkthrough />} />
      <Route path="/kennenlernen" element={<Kennenlernen />} />
      <Route path="/abo-finden" element={<AboErlebnis />} />
      <Route path="/2fa-pflicht" element={<Force2FASetup />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/preise" element={<Preise />} />
      <Route path="/pricing" element={<Preise />} />
      <Route path="/pocket" element={<Protected><Pocket /></Protected>} />
      <Route path="/upload/:token" element={<PublicUpload />} />

      {/* Öffentliche, login-freie Meta-Einladung (Variante C) */}
      <Route path="/meta-connect/abgeschlossen" element={<MetaConnectDone />} />
      <Route path="/meta-connect/:token" element={<MetaConnectPublic />} />

      {/* Sicherer administrativer Passwort-Reset (öffentlich, Token-basiert) */}
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* Einmaliger Notfall-Zugang (nur eingeloggter KEWAG-Admin) */}
      <Route path="/notfall-zugang" element={<Protected><BreakGlass /></Protected>} />
      {/* Normale „Passwort vergessen?"-Funktion (Cyon-Prinzip, öffentlich) */}
      <Route path="/passwort-vergessen" element={<ForgotPassword />} />
      <Route path="/passwort-zuruecksetzen" element={<PasswortZuruecksetzen />} />

      {/* Konto-Wiederherstellung (recovery_guardian): isoliertes Notfall-Konto */}
      <Route path="/kontowiederherstellung" element={<Protected><RecoveryOnly><RecoveryGuardian /></RecoveryOnly></Protected>} />
      {/* Öffentlicher Wiederherstellungs-Reset (Token-basiert) */}
      <Route path="/wiederherstellung-reset" element={<RecoveryReset />} />
      {/* Notfall-Wiederherstellung einrichten (nur Systeminhaber) */}
      <Route path="/notfall-wiederherstellung" element={<Protected><SuperAdminOnly><RecoverySetup /></SuperAdminOnly></Protected>} />

      {/* Kunden-Cockpit — eigene, dedizierte App-Shell (echte Kunden + Staff-Kundensicht) */}
      <Route
        path="/cockpit"
        element={
          <Protected>
            <CockpitGuard>
              <CockpitShell />
            </CockpitGuard>
          </Protected>
        }
      >
        <Route index element={<Navigate to="/cockpit/dashboard" replace />} />
        <Route path="dashboard" element={<ClientCockpit />} />
        <Route path="beitraege" element={<ClientBeitraege />} />
        <Route path="freigaben" element={<Freigaben />} />
        <Route path="kalender" element={<KalenderCockpit />} />
        <Route path="report" element={<Report />} />
        <Route path="meine-rechnungen" element={<ClientRechnungen />} />
        <Route path="abo-speicher" element={<AboSpeicher />} />
        <Route path="kanaele" element={<ChannelAddons />} />
        <Route path="support" element={<SupportCockpit />} />
        <Route path="einstellungen" element={<Einstellungen />} />
        <Route path="speicher-medien" element={<SpeicherMedien />} />
        <Route path="medien" element={<JuryMedien />} />
        <Route path="integrationen" element={<JuryIntegrationen />} />
        <Route path="kewag-control-os" element={<JuryControlOS />} />
        <Route path="pocket" element={<JuryPocket />} />
      </Route>

      {/* KEWAG Control OS / Team-App — Staff-Shell */}
      <Route
        path="/"
        element={
          <Protected>
            <StaffShellGuard>
              <AppShell />
            </StaffShellGuard>
          </Protected>
        }
      >
        <Route index element={<RoleFallback />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="kalender" element={<KalenderPro />} />
        <Route path="freigaben" element={<Inhalte reviewOnly />} />
        <Route path="report" element={<Report />} />
        <Route path="support" element={<Support />} />
        <Route path="speicher-medien" element={<SpeicherMedien />} />
        <Route path="einstellungen" element={<Einstellungen />} />
        <Route path="audit-log" element={<StaffOnly><AuditLog /></StaffOnly>} />
        <Route path="kunden" element={<FinanceOnly><Kunden /></FinanceOnly>} />
        <Route path="kunden/neu" element={<AdminOnly><KundeAnlegen /></AdminOnly>} />
        <Route path="kunden/:tenantId" element={<FinanceOnly><KundenDetail /></FinanceOnly>} />
        <Route path="leads" element={<StaffOnly><Leads /></StaffOnly>} />
        <Route path="wordpress" element={<StaffOnly><WordPress /></StaffOnly>} />
        <Route path="integrationen" element={<AdminOnly><Integrationen /></AdminOnly>} />
        <Route path="abos" element={<FinanceOnly><Abos /></FinanceOnly>} />
        <Route path="foto-auftraege" element={<StaffOnly><FotoAuftraege /></StaffOnly>} />
        <Route path="inhalte" element={<StaffOnly><Inhalte /></StaffOnly>} />
        <Route path="aufgaben" element={<StaffOnly><Aufgaben /></StaffOnly>} />
        <Route path="system-health" element={<StaffOnly><SystemHealth /></StaffOnly>} />
        <Route path="marken-profil" element={<StaffOnly><MarkenProfil /></StaffOnly>} />
        <Route path="rollen" element={<AdminOnly><RollenRechte /></AdminOnly>} />
        <Route path="nachrichten" element={<AdminOnly><Nachrichten /></AdminOnly>} />
        <Route path="rechnungen" element={<FinanceOnly><Rechnungen /></FinanceOnly>} />
        <Route path="finanzen" element={<FinanceOnly><FinanzDashboard /></FinanceOnly>} />
        <Route path="abrechnungseinstellungen" element={<FinanceOnly><Abrechnungseinstellungen /></FinanceOnly>} />
        <Route path="demo-anfragen" element={<SuperAdminOnly><DemoAnfragen /></SuperAdminOnly>} />
        <Route path="demo-zugaenge" element={<SuperAdminOnly><DemoZugaenge /></SuperAdminOnly>} />
        <Route path="kundenfeedback" element={<StaffOnly><Kundenfeedback /></StaffOnly>} />
        <Route path="zahlungen" element={<FinanceOnly><Zahlungen /></FinanceOnly>} />
        <Route path="zusatzleistungen" element={<AdminOnly><Zusatzleistungen /></AdminOnly>} />
        <Route path="backups" element={<AdminOnly><Backups /></AdminOnly>} />
      </Route>
      <Route path="*" element={<RoleFallback />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
